// 🆕 Feb 2026 — Helper globale per download allegati con barra di avanzamento.
// Espone una funzione downloadWithProgress che gestisce automaticamente:
//  - Tentativo 1: signed URL GCS (window.open, progress indeterminato)
//  - Tentativo 2: fallback blob via API con onDownloadProgress → percentuale reale
//  - Aggiornamenti UI via EventTarget globale (DownloadProgressOverlay ascolta)
//  - Gestione errori con messaggio chiaro (toast)
// L'estrazione allegati AI usa runExtraction() che mostra progress simulato (0→90%) durante
// la chiamata backend, poi 100% al termine.
import { api } from "./api";
import { toast } from "sonner";

const bus = new EventTarget();
// Stato interno: mappa jobId → { id, label, phase: "downloading" | "extracting" | "done" | "error", percent: 0-100 | null, loaded, total, message }
const jobs = new Map();

const emit = () => bus.dispatchEvent(new CustomEvent("update", { detail: Array.from(jobs.values()) }));

export const subscribeDownloadProgress = (handler) => {
  const listener = (ev) => handler(ev.detail || []);
  bus.addEventListener("update", listener);
  // Emit iniziale
  handler(Array.from(jobs.values()));
  return () => bus.removeEventListener("update", listener);
};

const upsert = (id, patch) => {
  const prev = jobs.get(id) || { id, phase: "downloading", percent: 0, label: "" };
  jobs.set(id, { ...prev, ...patch });
  emit();
};

const removeSoon = (id, ms = 1500) => {
  setTimeout(() => {
    jobs.delete(id);
    emit();
  }, ms);
};

const _newId = () => `dl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Download di un allegato con barra di avanzamento reale.
 * @param {Object} opts
 * @param {string} opts.label - Nome file mostrato nella barra (es. "polizza_2026.pdf").
 * @param {string} [opts.signedUrlPath] - Path API che restituisce { url } signed GCS (bypass). Opzionale.
 * @param {string} opts.blobPath - Path API che restituisce il blob (fallback / percorso principale).
 * @param {string} [opts.filename] - Nome del file salvato (default: label).
 */
export async function downloadWithProgress({ label, signedUrlPath, blobPath, filename }) {
  const id = _newId();
  const jobLabel = label || filename || "allegato";
  upsert(id, { label: jobLabel, phase: "downloading", percent: 0, loaded: 0, total: null });

  try {
    // Tentativo 1: signed URL GCS → apri in nuova tab (progress indeterminato lato server GCS)
    if (signedUrlPath) {
      try {
        const { data } = await api.get(signedUrlPath);
        if (data?.url) {
          upsert(id, { phase: "done", percent: 100 });
          window.open(data.url, "_blank", "noopener,noreferrer");
          removeSoon(id);
          return;
        }
      } catch (sigErr) {
        // 409 → file legacy → fallback blob. Altri errori → rilancio dopo che tento il blob.
        if (sigErr?.response?.status && sigErr.response.status !== 409) {
          // Non fatale: procediamo comunque col blob
        }
      }
    }

    // Tentativo 2: fallback blob con progress reale
    const resp = await api.get(blobPath, {
      responseType: "blob",
      onDownloadProgress: (e) => {
        if (e.total && e.total > 0) {
          const percent = Math.min(100, Math.round((e.loaded / e.total) * 100));
          upsert(id, { percent, loaded: e.loaded, total: e.total });
        } else {
          // Progress indeterminato: mostriamo i byte caricati senza percentuale
          upsert(id, { percent: null, loaded: e.loaded, total: null });
        }
      },
    });

    // Trigger download nel browser
    const blob = resp.data instanceof Blob ? resp.data : new Blob([resp.data]);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || jobLabel;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 30 * 1000);

    upsert(id, { phase: "done", percent: 100 });
    removeSoon(id);
  } catch (err) {
    const message = err?.response?.data?.detail || err?.message || "Errore durante il download";
    upsert(id, { phase: "error", message });
    toast.error(`Download fallito: ${message}`);
    removeSoon(id, 4000);
    throw err;
  }
}

/**
 * Upload multipart con barra di avanzamento reale (byte inviati / totale).
 * Usa axios.onUploadProgress.
 * @param {Object} opts
 * @param {string} opts.label - Etichetta della barra (es. "Upload allegato.pdf")
 * @param {(config: {onUploadProgress: (e: ProgressEvent) => void}) => Promise<any>} opts.run
 *   Funzione che riceve la config con onUploadProgress ed esegue la chiamata multipart.
 * @returns {Promise<any>}
 */
export async function uploadWithProgress({ label, run }) {
  const id = _newId();
  upsert(id, { label: label || "Upload in corso", phase: "uploading", percent: 0, loaded: 0, total: null });
  try {
    const result = await run({
      onUploadProgress: (e) => {
        if (e.total && e.total > 0) {
          const percent = Math.min(100, Math.round((e.loaded / e.total) * 100));
          upsert(id, { percent, loaded: e.loaded, total: e.total });
        } else {
          upsert(id, { percent: null, loaded: e.loaded, total: null });
        }
      },
    });
    upsert(id, { phase: "done", percent: 100 });
    removeSoon(id, 1200);
    return result;
  } catch (err) {
    const message = err?.response?.data?.detail || err?.message || "Errore upload";
    upsert(id, { phase: "error", message });
    toast.error(`Upload fallito: ${message}`);
    removeSoon(id, 4000);
    throw err;
  }
}

/**
 * Wrapper generico per operazioni di estrazione/parsing di allegati che non hanno progress reale.
 * Simula un avanzamento visivo 0→90% durante la richiesta backend, poi 100% al completamento.
 * @param {Object} opts
 * @param {string} opts.label - Etichetta della barra ("Estrazione libretto in corso…")
 * @param {() => Promise<any>} opts.run - La promise dell'operazione (chiamata backend).
 * @returns {Promise<any>}
 */
export async function runExtractionWithProgress({ label, run }) {
  const id = _newId();
  upsert(id, { label: label || "Estrazione in corso", phase: "extracting", percent: 5 });
  // Simulazione lineare capped a 90% mentre attendiamo il backend
  const interval = setInterval(() => {
    const cur = jobs.get(id);
    if (!cur) return;
    if (cur.phase !== "extracting") return;
    const next = Math.min(90, (cur.percent || 5) + 5);
    upsert(id, { percent: next });
  }, 800);
  try {
    const result = await run();
    clearInterval(interval);
    upsert(id, { phase: "done", percent: 100 });
    removeSoon(id, 1200);
    return result;
  } catch (err) {
    clearInterval(interval);
    const message = err?.response?.data?.detail || err?.message || "Errore durante l'estrazione";
    upsert(id, { phase: "error", message });
    toast.error(`Estrazione fallita: ${message}`);
    removeSoon(id, 4000);
    throw err;
  }
}

/**
 * 🆕 Feb 2026 — POST che restituisce un blob (es. rendering PDF template), con progress reale.
 * Combina due fasi in un'unica barra:
 *   1. "Generazione…" (progress simulato 0→80%) mentre il backend elabora
 *   2. "Download…"   (progress reale 80→100%) durante lo stream della response
 * Il file viene automaticamente scaricato nel browser al completamento.
 *
 * @param {Object} opts
 * @param {string} opts.label - Etichetta iniziale (es. "Generazione PDF: Modulo Niam…")
 * @param {string} opts.filename - Nome file scaricato (es. "documento.pdf")
 * @param {(config: {responseType: "blob", onDownloadProgress: (e: ProgressEvent) => void}) => Promise<any>} opts.run
 *   Funzione che riceve la config axios ed esegue la POST (deve ritornare la Promise Axios).
 * @returns {Promise<any>} axios response
 */
export async function postDownloadWithProgress({ label, filename, run }) {
  const id = _newId();
  upsert(id, { label: label || filename || "Generazione in corso", phase: "extracting", percent: 5 });

  // Fase 1: crescita asintotica verso 80% (mai bloccata) — se il backend è lento la barra
  // continua a muoversi lentamente invece di fermarsi a un valore fisso.
  const interval = setInterval(() => {
    const cur = jobs.get(id);
    if (!cur) return;
    if (cur.phase !== "extracting") return;
    const p = cur.percent || 5;
    // Ogni tick si avvicina del ~15% della distanza da 80 → converge asintoticamente
    const next = Math.min(79, p + Math.max(1, Math.round((80 - p) * 0.15)));
    upsert(id, { percent: next });
  }, 500);

  try {
    const resp = await run({
      responseType: "blob",
      onDownloadProgress: (e) => {
        // Appena arrivano byte reali, passiamo alla fase "downloading" con progress reale
        clearInterval(interval);
        if (e.total && e.total > 0) {
          // Mappa 0..100% reale → 80..100% visualizzato
          const realPct = Math.min(100, Math.round((e.loaded / e.total) * 100));
          const shown = 80 + Math.round((realPct * 20) / 100);
          upsert(id, { phase: "downloading", percent: shown, loaded: e.loaded, total: e.total });
        } else {
          upsert(id, { phase: "downloading", percent: null, loaded: e.loaded });
        }
      },
    });
    clearInterval(interval);

    // Trigger download nel browser
    const blob = resp.data instanceof Blob ? resp.data : new Blob([resp.data]);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "documento.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 30 * 1000);

    upsert(id, { phase: "done", percent: 100 });
    removeSoon(id);
    return resp;
  } catch (err) {
    clearInterval(interval);
    const message = err?.response?.data?.detail || err?.message || "Errore durante la generazione";
    upsert(id, { phase: "error", message });
    toast.error(`Generazione fallita: ${message}`);
    removeSoon(id, 4000);
    throw err;
  }
}
